import classNames from "classnames";
import { FormikErrors, FormikProps } from "formik";
import React, { useEffect, useRef, useState } from "react";
import { DeliveryArea, DELIVERY_METHODS, StoreInterface, WhatsappCheckoutOption } from "../../../assets/interfaces";
import { extractErrorValues, getFieldvalues, shuffleArray, toCurrency } from "../../../assets/js/utils/functions";
import { AppBtn } from "../../ui/buttons";
import DataAccordion from "../../ui/data-accordion";
import ErrorLabel from "../../ui/error-label";
import { InputField, PhoneInput, SelectDropdown, TextArea } from "../../ui/form-elements";
import SelectDropdownWithModal from "../../ui/form-elements/select-dropdown-with-modal";
import { ModalBody, ModalFooter } from "../../ui/modal";
import { CheckoutForm, DEFAULT_ADDRESS_CACHE } from "../modals/checkout";
import CartContext, { FormatCurrencyFun } from "../../../contexts/cart-context";
import Portal from "@/components/portal";
import { useModals } from "@/components/hooks/useModals";
import PreviousAddressModal from "./previous-address-modal";
import { useLocalObject } from "@/components/hooks/useLocalState";
import { getFormComponent } from "./custom-form";
import { getFullProductName } from "./cart";
import { resolvePhone } from "@/assets/js/utils/utils";
import { AddressCache, DeliveryMethodBtn, optionsFromDA } from "./customer-details";
import CartContextV2 from "@/contexts/cart-context-v2";

type ModalState = { [string: string]: { show: boolean } };

interface Props {
  extraDetailsForm: StoreInterface["configuration"]["custom_checkout_form"];
  store: StoreInterface;
  prevStep: VoidFunction;
  nextStep: VoidFunction;
  scrollToTop?: boolean;
  form: FormikProps<CheckoutForm>;
  createOrderReq: any;
  formatAsCurrency: FormatCurrencyFun;
}

const EnquiryDetails: React.FC<Props> = ({
  store,
  prevStep,
  scrollToTop,
  form,
  createOrderReq,
  nextStep,
  extraDetailsForm,
  formatAsCurrency,
}) => {
  const { priceUnformatted, currencies, price, cartLink, cart } = CartContextV2.useContainer();
  const [errorText, setErrorText] = useState("");
  const { modals, toggleModal } = useModals(["coupon", "address"]);
  let modalBodyRef = useRef<HTMLDivElement>(null);

  const deliveryAreas = store.delivery_areas as DeliveryArea[];
  const { deliveringToSelf, deliveryMethod } = form.values;
  const hasCustomCheckoutForm = store?.configuration?.custom_checkout_form?.length > 0;

  useEffect(() => {
    scrollToTop && modalBodyRef.current.scrollTo(0, 0);
  }, [scrollToTop]);

  const checkoutOptions = shuffleArray<WhatsappCheckoutOption>(store?.checkout_channels?.whatsapp);

  const getEnquiryLink = (prefix: string, phone: string) => {
    var extra =
      "\n\n*EXTRA DETAILS:*\n\n" +
      Object.keys(form.values.extra_details)
        .map((key) => `*${key?.replace(/_/g, " ").trim()}*: ${form.values.extra_details[key]}`)
        .join("\n");
    return encodeURI(
      `https://api.whatsapp.com/send/?phone=${resolvePhone(phone)}&text=${prefix}\n\n${cart
        .map((c) => (c.is_deleted || c.is_unavailable ? "" : `📦 ${getFullProductName(c)} - ${c.quantity}x\n`))
        .join("")}\n\n💰 Total price: *${price}*\n\nCheck out cart here:\n${cartLink} ${extra}`
    );
  };
  const submitForm = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    window.open(
      getEnquiryLink("Hi, i'll like to make an enquiry about these items:", resolvePhone(checkoutOptions[0].phone))
    );

    return;

    if (deliveringToSelf === undefined) {
      setErrorText("Please provide your delivery information");
      return;
    }
    const errors = await form.validateForm();
    var { extra_details, ...rest } = errors;
    if (Object.keys(rest)?.length > 0) {
      const error = extractErrorValues(rest as FormikErrors<any>);
      setErrorText(error);
      modalBodyRef.current.scrollTo(0, 0);
      return;
    }

    // form.handleSubmit(e);
    nextStep();
  };

  const changeDeliveryMethod = (method: DELIVERY_METHODS) => {
    if (!deliveringToSelf && method === DELIVERY_METHODS.PICKUP) {
      form.setFieldValue("deliveringToSelf", true);
    }

    form.setFieldValue("deliveryMethod", method);
  };

  const handlePreviousAddress = (address: string) => {
    form.setFieldValue("address", address);
    toggleModal("address");
  };

  return (
    <>
      <form className="flex flex-col flex-auto overflow-hidden" onSubmit={submitForm}>
        <ModalBody setRef={(el) => (modalBodyRef.current = el)} className="relative">
          <p className="text-black-muted text-sm md:text-1sm mb-3.75">
            Please fill in the following information to make an enquiry on whatsapp.
          </p>
          <ErrorLabel error={createOrderReq?.error?.message ?? errorText} setError={setErrorText} />
          <DataAccordion title="Purchase Information">
            {store?.configuration?.customer_pickup_enabled && store?.configuration?.require_delivery_info && (
              <div>
                <div className="my-3.75">
                  <h4 className="font-display font-semibold text-black-secondary rounded-5 block">
                    How will you like to get your order?
                  </h4>
                </div>

                <div className="flex items-center space-x-2.5">
                  <DeliveryMethodBtn
                    onClick={(e) => changeDeliveryMethod(DELIVERY_METHODS.DELIVERY)}
                    active={deliveryMethod === DELIVERY_METHODS.DELIVERY}
                    label="Delivery"
                  />

                  <DeliveryMethodBtn
                    onClick={(e) => changeDeliveryMethod(DELIVERY_METHODS.PICKUP)}
                    active={deliveryMethod === DELIVERY_METHODS.PICKUP}
                    label="Pickup"
                  />
                </div>
              </div>
            )}
            {deliveryMethod === DELIVERY_METHODS.DELIVERY && (
              <div className="-mb-2 mt-7.5">
                <SelectDropdown
                  options={[
                    { text: "Myself", value: "true" },
                    { text: "Someone Else", value: "false" },
                  ]}
                  className="mt-5"
                  onChange={(e) => form.setFieldValue("deliveringToSelf", e.target.value === "true")}
                  label=" Who will receive this item?"
                  value={form.values.deliveringToSelf === true ? "true" : "false"}
                  // {...getFieldvalues("deliveringToSelf", form)}
                />
              </div>
            )}

            <div className="mt-[27.5px]">
              {deliveringToSelf !== undefined && (
                <EnquiryDeliveryDetailsForm
                  {...{
                    deliveryiesEnabled: store.deliveries_enabled,
                    deliveryAreas,
                    form,
                    deliveringToSelf,
                    formatAsCurrency,
                    toggleModal,
                    emailRequired: store.configuration?.require_emails,
                  }}
                />
              )}
            </div>
            <DataAccordion title="Extra Information">
              <div className={`mt-3.75 space-y-5 ${extraDetailsForm?.length > 2 ? "" : "pb-20"}`}>
                {extraDetailsForm?.map((component, idx) => (
                  <div key={idx}>
                    {getFormComponent(component, form, formatAsCurrency, currencies.active, "extra_details")}
                  </div>
                ))}

                <div>
                  <DataAccordion deactivated title="Do you have any notes or special instructions?">
                    <TextArea className="!mt-5" label="Order Notes" rows={3} {...getFieldvalues("orderNotes", form)} />
                  </DataAccordion>
                </div>
              </div>
            </DataAccordion>
          </DataAccordion>
        </ModalBody>
        <ModalFooter>
          <div className="flex gap-2.5 w-full">
            {/* <AppBtn onClick={prevStep} className="flex-1" color="neutral" size="lg">
              Go Back
            </AppBtn> */}
            <AppBtn className="flex-1" isBlock type="submit" size="lg">
              Make Enquiry on Whatsapp
            </AppBtn>
          </div>
        </ModalFooter>
      </form>
      <Portal>
        <PreviousAddressModal
          toggle={() => toggleModal("address")}
          onSelect={handlePreviousAddress}
          show={modals.address}
          address={form.values.address}
          deliveriesEnabled={store?.deliveries_enabled}
        />
      </Portal>
    </>
  );
};

interface DeliveryFormProps {
  form: FormikProps<CheckoutForm>;
  deliveringToSelf: boolean;
  deliveryiesEnabled: boolean;
  deliveryAreas: DeliveryArea[];
  formatAsCurrency: FormatCurrencyFun;
  toggleModal: (modal: string) => void;
  emailRequired?: boolean;
}

export const EnquiryDeliveryDetailsForm: React.FC<DeliveryFormProps> = ({
  form,
  deliveringToSelf,
  deliveryAreas,
  deliveryiesEnabled,
  formatAsCurrency,
  toggleModal,
  emailRequired = false,
}) => {
  const [addresses] = useLocalObject<AddressCache>("addresses", DEFAULT_ADDRESS_CACHE);
  const customerIsPickingUp = form.values.deliveryMethod === DELIVERY_METHODS.PICKUP;
  const deliveryNotRequired = form.values.deliveryMethod === DELIVERY_METHODS.NONE;

  const addressListToUse = addresses[deliveryiesEnabled ? "validated" : "userInputed"];

  return (
    <div
      onClick={(e) => {
        e.preventDefault();
      }}
    >
      <DataAccordion deactivated title="Your Details">
        <div className="pt-5 !space-y-6">
          <InputField label="Full Name" {...getFieldvalues("name", form)} />
          <PhoneInput label="Your Whatsapp Number" {...getFieldvalues("phone", form)} />
          <InputField
            label={`Email Address ${!emailRequired ? "(optional)" : ""}`}
            required={emailRequired}
            type="email"
            {...getFieldvalues("email", form)}
          />
        </div>
      </DataAccordion>
      {!customerIsPickingUp && !deliveryNotRequired && (
        <DataAccordion deactivated title={deliveringToSelf ? "Delivery Details" : "Receiver's Details"}>
          <div className="pt-5">
            {!deliveringToSelf && (
              <>
                <InputField label="Receiver's Full Name" {...getFieldvalues("deliveryContact.name", form)} />
                <PhoneInput label="Receiver's Whatsapp Number" {...getFieldvalues("deliveryContact.phone", form)} />
              </>
            )}
            {deliveryAreas.length > 0 && (
              <SelectDropdownWithModal
                options={optionsFromDA(deliveryAreas, formatAsCurrency)}
                label="Select Delivery Area"
                hasSearch
                searchLabel="Search areas"
                {...getFieldvalues("area", form)}
              />
            )}
            {<TextArea className="!mt-5" label="Delivery Address" rows={3} {...getFieldvalues("address", form)} />}

            {addressListToUse && addressListToUse?.length > 0 && (
              <button
                onClick={() => toggleModal("address")}
                className="flex ml-auto items-center justify-center font-medium whitespace-nowrap bg-white text-primary-500 mt-2.5 text-1xs sm:text-sm rounded-10 undefined font-action !outline-none transition-all cursor-pointer box-border group flex-shrink-0"
                type="button"
              >
                Select From Previous Address
                {/* prettier-ignore */}
                <svg viewBox="0 0 15 16" fill="none" className="w-3.75 ml-px mt-px">
                  <path d="M3.96484 11.5355L11.0359 4.46446" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"></path>
                  <path d="M11.0352 11.5355L11.0352 4.46446L3.96409 4.46445" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"></path>
                </svg>
              </button>
            )}
          </div>
        </DataAccordion>
      )}

      {customerIsPickingUp && (
        <div className="flex items-start rounded-8 p-4.5 sm:p-5 bg-grey-fields-100 w-full">
          <div className="flex-shrink-0 mr-3 sm:mr-3.75">
            <figure className="rounded-full w-8 h-8 sm:h-10 sm:w-10 text-accent-green-500 bg-white shadow-card flex items-center justify-center">
              <svg width="36" height="36" viewBox="0 0 36 36" fill="none">
                <rect width="36" height="36" rx="18" fill="white" />
                <path
                  d="M22.773 22.0229L18 26.7959L13.227 22.0229C12.283 21.0789 11.6401 19.8762 11.3797 18.5668C11.1193 17.2575 11.2529 15.9003 11.7638 14.6669C12.2747 13.4335 13.1399 12.3793 14.2499 11.6376C15.3599 10.8959 16.665 10.5 18 10.5C19.335 10.5 20.6401 10.8959 21.7501 11.6376C22.8601 12.3793 23.7253 13.4335 24.2362 14.6669C24.7471 15.9003 24.8808 17.2575 24.6203 18.5668C24.3599 19.8762 23.717 21.0789 22.773 22.0229ZM18 18.7499C18.3978 18.7499 18.7794 18.5919 19.0607 18.3106C19.342 18.0293 19.5 17.6478 19.5 17.2499C19.5 16.8521 19.342 16.4706 19.0607 16.1893C18.7794 15.908 18.3978 15.7499 18 15.7499C17.6022 15.7499 17.2206 15.908 16.9393 16.1893C16.658 16.4706 16.5 16.8521 16.5 17.2499C16.5 17.6478 16.658 18.0293 16.9393 18.3106C17.2206 18.5919 17.6022 18.7499 18 18.7499Z"
                  fill="#39B588"
                />
              </svg>
            </figure>
          </div>
          <div className="w-full">
            <div className="flex justify-between items-start">
              <span className="text-sm sm:text-sm text-black-muted">
                Random Store, Rd. 3, Lekki Paradise 2 Estate, Lekki, Lagos State{" "}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EnquiryDetails;
